import React, { useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { useAppSelector } from '@/store';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { selectLeaseIds, isUserLeaseHolder } from '@/store/selectors/userSelectors';
import { linkedAcctsConnections, linkedAcctsConnectionsLoading, selectAllLeaseIds } from '@/store/selectors/linkedAccountsSelectors';
import { UndergoingMaintenancePage } from '@/components/residents/maintenance/UndergoingMaintenancePage';
import { useEntrataIntegrationDisabled } from '@/features/payments/hooks/useEntrataIntegrationDisabled';
import {  useGetAutoPayStatusQuery, useCancelAutoPayMutation } from '@/store/api';
import { DesktopSectionHeader } from '@/components/residents/desktop/myAccount/DesktopSectionHeader';
import LeaseAutopayCard from '@/components/residents/autopay/LeaseAutopayCard';
import { strings } from '@/constants/residents/strings';

export const AutopayView: React.FC = () => {
  const navigation = useNavigation<any>();
  const [refreshNonce, setRefreshNonce] = React.useState(0);
  const ownLeaseIds = useAppSelector(selectLeaseIds);
  const connectionLeaseIds = useAppSelector(selectAllLeaseIds);
  const connections = useAppSelector(linkedAcctsConnections);
  const connectionsFetchLoading = useAppSelector(linkedAcctsConnectionsLoading);
  const userIsLeaseHolder = useAppSelector(isUserLeaseHolder);

  // Feature flag similar to web (Entrata maintenance)
  const entrataDisabled = useEntrataIntegrationDisabled();

  const selfList = ownLeaseIds;
  const connList = useMemo(() => connectionLeaseIds.filter((id) => !ownLeaseIds.includes(id)), [connectionLeaseIds, ownLeaseIds]);

  // Build a map of leaseId -> connection user info (email/name) if available
  const connectionUserByLeaseId = useMemo(() => {
    const map = new Map<number, any>();
    (connections || []).forEach((conn: any) => {
      (conn?.leaseIds || []).forEach((id: number) => map.set(id, conn.user));
    });
    return map;
  }, [connections]);

  // Autopay status list (all leases)
  const {  refetch: autoPayStatusRefetch } = useGetAutoPayStatusQuery();
  const [cancelAutoPay] = useCancelAutoPayMutation();

  const onOpenWizard = (leaseId: number) => {
    navigation.navigate('AutoPayWizard', { leaseId });
  };

  // Refresh autopay status when screen regains focus (after edit/manage flow)
  useFocusEffect(
    React.useCallback(() => {
      autoPayStatusRefetch();
      setRefreshNonce((n) => n + 1);
      return () => {};
    }, [autoPayStatusRefetch])
  );

  if (entrataDisabled) {
    return <UndergoingMaintenancePage />;
  }

  return (
    <View style={styles.root}>
      <DesktopSectionHeader
        title={strings.account.autopayTitle}
        containerStyle={styles.header}
        titleStyle={styles.headerTitle}
      />

      {!userIsLeaseHolder && selfList.length === 0 && connList.length === 0 ? (
        <View style={[styles.emptyState, { flex: 1 }]}>
          <Text style={styles.emptyTitle}>{strings.account.autopayEmptyTitle}</Text>
          <Text style={styles.emptyMessage}>{strings.account.autopayEmptyMessage}</Text>
        </View>
      ) : (
        <ScrollView style={styles.body} contentContainerStyle={styles.bodyInner}>
          {selfList.length > 0 && (
            <View style={styles.section}>
              {selfList.map((id) => (
                <LeaseAutopayCard
                  key={`self-${id}`}
                  leaseId={id}
                  user={null}
                  onManage={() => onOpenWizard(id)}
                  onRemove={async (autopayId: number) => {
                    await cancelAutoPay(autopayId).unwrap();
                    autoPayStatusRefetch();
                  }}
                  onRefresh={autoPayStatusRefetch}
                  refreshNonce={refreshNonce}
                />
              ))}
            </View>
          )}

          {connList.length > 0 && (
            <View style={styles.section}>
              {connList.map((id) => (
                <LeaseAutopayCard
                  key={`conn-${id}`}
                  leaseId={id}
                  user={connectionUserByLeaseId.get(id) || null}
                  onManage={() => onOpenWizard(id)}
                  onRemove={async (autopayId: number) => {
                    await cancelAutoPay(autopayId).unwrap();
                    autoPayStatusRefetch();
                  }}
                  onRefresh={autoPayStatusRefetch}
                  refreshNonce={refreshNonce}
                />
              ))}
            </View>
          )}
        </ScrollView>
      )}

      {connectionsFetchLoading && (
        <View style={{ alignItems: 'center', justifyContent: 'center', paddingVertical: 32 }}>
          <Text style={{ color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY }}>{strings.account.labels.loading}</Text>
        </View>
      )}
    </View>
  );
};

// inlined card moved to components/residents/autopay/LeaseAutopayCard to slim this file

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND },
  header: { borderBottomWidth: 1, borderBottomColor: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY, paddingHorizontal: 20, paddingVertical: 14 },
  headerTitle: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontWeight: '700', fontSize: 18 },
  body: { flex: 1 },
  bodyInner: { padding: 16, gap: 16 },
  emptyState: { gap: 8, alignItems: 'center', paddingVertical: 24 },
  emptyTitle: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontWeight: '600' },
  emptyMessage: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, opacity: 0.8, fontSize: 14, textAlign: 'center' },
  section: { gap: 12 },
  summaryCard: { backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND, borderWidth: 1, borderColor: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY, borderRadius: 12, padding: 16, gap: 10 },
  summaryHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  summaryTitle: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontWeight: '600', fontSize: 16 },
  summaryManageBtn: { borderWidth: 1, borderColor: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY, paddingHorizontal: 12, paddingVertical: 6, borderRadius: 10 },
  summaryManageText: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontWeight: '600' },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6 },
  summaryLabel: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontSize: 14 },
  summaryValue: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontSize: 14 },
  avatarCircle: { width: 48, height: 48, borderRadius: 24, backgroundColor: '#FFFFFF1A', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  avatarImg: { width: 48, height: 48 },
  badge: { alignSelf: 'flex-start', backgroundColor: RESIDENTS_DESKTOP_COLORS.CIRCLE_ICON_BACKGROUND, borderWidth: 1, borderColor: '#374151', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 9999, marginBottom: 4 },
  badgeText: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontSize: 12 },
  userTitle: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontWeight: '800', marginBottom: 4 },
  propertyText: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY },
  primaryBtn: { backgroundColor: RESIDENTS_DESKTOP_COLORS.BUTTON_TERTIARY, paddingVertical: 10, paddingHorizontal: 14, borderRadius: 8, alignSelf: 'flex-start' },
  primaryBtnText: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontWeight: '700' },
});

export default AutopayView;


